"use client";

import * as React from "react";
import { AssistantRuntimeProvider, type ChatModelAdapter, useLocalRuntime } from "@assistant-ui/react";
import { Thread } from "@/components/assistant-ui/thread";

export default function SlideChartTimePeriodChat() {
  const initialMessages = React.useMemo(
    () => [
      {
        role: "assistant" as const,
        content: [
          {
            type: "text" as const,
            text: "Hi — I can help you change the **chart time period** on this slide (e.g. daily vs. monthly, or a custom date range). Tell me the period you want and I’ll draft the update.",
          },
        ],
      },
    ],
    []
  );

  const adapter: ChatModelAdapter = React.useMemo(
    () => ({
      async *run({ messages }) {
        const lastMessage = messages[messages.length - 1];
        let userInput = "";

        if (lastMessage?.content && Array.isArray(lastMessage.content)) {
          const textPart = lastMessage.content.find(
            (part) =>
              typeof part === "object" &&
              part !== null &&
              "text" in part &&
              typeof (part as { text?: string }).text === "string"
          );
          if (textPart) userInput = (textPart as { text: string }).text;
        }

        try {
          window.dispatchEvent(new CustomEvent("ai-run-start"));
        } catch {
          /* ignore */
        }

        const topic = "Chart Time Period";
        const response = userInput.trim()
          ? `Got it. For **${topic}**, we will connect this chat to the slide-edit assistant next. Your message: “${userInput.slice(0, 200)}${
              userInput.length > 200 ? "…" : ""
            }”`
          : `Let’s refine **${topic}** for this slide. Tell me what period you want (e.g. 1D/1W/1M/1Y/5Y) and any key dates to highlight.`;

        const STREAM_CHAR_DELAY_MS = 4;
        const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
        for (let i = 1; i <= response.length; i++) {
          if (i === 1) {
            try {
              window.dispatchEvent(new CustomEvent("ai-first-token"));
            } catch {
              /* ignore */
            }
          }
          yield { content: [{ type: "text" as const, text: response.slice(0, i) }] };
          await delay(STREAM_CHAR_DELAY_MS);
        }

        try {
          window.dispatchEvent(new CustomEvent("ai-run-end"));
        } catch {
          /* ignore */
        }
      },
    }),
    []
  );

  const runtime = useLocalRuntime(adapter, { initialMessages });

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <div className="flex h-full min-h-0 flex-col text-sm leading-snug text-gray-900 [&_.aui-md]:text-sm">
        <Thread />
      </div>
    </AssistantRuntimeProvider>
  );
}

